import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  Search as SearchIcon,
  VisibilityOutlined as ViewIcon,
  PeopleOutlineRounded as EmptyIcon
} from '@mui/icons-material';
import { sileo } from 'sileo';
import { patientService } from '../services/patientService';
import { LoadingOrb } from './LoadingOrb';
import { Patient, PatientFilters, GENDER_OPTIONS, SORT_OPTIONS } from '../types/patient';
import PatientForm from './PatientForm';

interface PatientListProps {
  onPatientSelect?: (patient: Patient) => void;
}

const PatientList: React.FC<PatientListProps> = ({ onPatientSelect }) => {
  // Estado de la lista
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Estado de paginación
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Estado de filtros
  const [filters, setFilters] = useState<PatientFilters>({
    search: '',
    sortBy: 'createdAt',
    order: 'DESC'
  });
  
  // Estado de modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);

  // Cargar pacientes
  const loadPatients = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await patientService.getPatients(page, limit, filters);
      setPatients(response.patients);
      setTotal(response.pagination.total);
      setTotalPages(response.pagination.totalPages);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Error al cargar pacientes');
      sileo.error({
        title: 'Error al cargar pacientes',
        description: 'No pudimos obtener el listado. Revisa tu conexión e intenta nuevamente.',
      });
    } finally {
      setLoading(false);
    }
  };

  // Cargar pacientes cuando cambien los filtros o la página
  useEffect(() => {
    loadPatients();
  }, [page, filters]);

  // Manejar cambio de página
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  // Manejar cambio de filtros
  const handleFilterChange = (field: keyof PatientFilters, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1); // Resetear a primera página
  };

  // Manejar búsqueda
  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPage(1);
  };

  // Limpiar filtros
  const clearFilters = () => {
    setFilters({
      search: '',
      sortBy: 'createdAt',
      order: 'DESC'
    });
    setPage(1);
  };

  // Crear paciente
  const handleCreatePatient = async (patientData: any) => {
    try {
      await patientService.createPatient(patientData);
      sileo.success({
        title: 'Paciente creado',
        description: 'El paciente se registró correctamente en el sistema.',
      });
      setShowCreateModal(false);
      loadPatients();
    } catch (error: any) {
      sileo.error({
        title: 'No se pudo crear el paciente',
        description: error.response?.data?.message || 'Ocurrió un error inesperado. Intenta nuevamente.',
      });
    }
  };

  // Editar paciente
  const handleEditPatient = async (patientData: any) => {
    if (!editingPatient) return;
    
    try {
      await patientService.updatePatient(editingPatient.id, patientData);
      sileo.success({
        title: 'Paciente actualizado',
        description: 'Los cambios se guardaron correctamente.',
      });
      setShowEditModal(false);
      setEditingPatient(null);
      loadPatients();
    } catch (error: any) {
      sileo.error({
        title: 'No se pudo actualizar el paciente',
        description: error.response?.data?.message || 'Ocurrió un error inesperado. Intenta nuevamente.',
      });
    }
  };

  // Eliminar paciente
  const handleDeletePatient = async () => {
    if (!selectedPatient) return;
    
    try {
      await patientService.deletePatient(selectedPatient.id);
      sileo.success({
        title: 'Paciente eliminado',
        description: 'El paciente fue marcado como inactivo.',
      });
      setShowDeleteModal(false);
      setSelectedPatient(null);
      loadPatients();
    } catch (error: any) {
      sileo.error({
        title: 'No se pudo eliminar el paciente',
        description: error.response?.data?.message || 'Ocurrió un error inesperado. Intenta nuevamente.',
      });
    }
  };

  // Abrir modal de edición
  const openEditModal = (patient: Patient) => {
    setEditingPatient(patient);
    setShowEditModal(true);
  };

  // Abrir modal de eliminación
  const openDeleteModal = (patient: Patient) => {
    setSelectedPatient(patient);
    setShowDeleteModal(true);
  };

  // Formatear fecha. `dateOfBirth` llega como "YYYY-MM-DD" (DATEONLY del backend);
  // parsearlo con `new Date(string)` lo interpreta como medianoche UTC y, en una
  // zona horaria detrás de UTC (ej. Chile), el formateo local retrocede un día.
  // Construir la fecha con año/mes/día evita ese corrimiento.
  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('es-CL');
  };

  // Obtener etiqueta de género
  const getGenderLabel = (gender: string) => {
    const option = GENDER_OPTIONS.find(opt => opt.value === gender);
    return option?.label || gender;
  };

  // Color de avatar determinístico según el id del paciente, para dar variedad visual
  const avatarPalette = ['#1877F2', '#8E33FF', '#00B8D9', '#22C55E', '#FFAB00', '#FF5630'];
  const getAvatarColor = (id: number) => avatarPalette[id % avatarPalette.length];

  if (loading && patients.length === 0) {
    return <LoadingOrb size={64} minHeight="400px" />;
  }

  return (
    <Box>
      {/* Encabezado de página */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
            Pacientes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {total > 0 ? `${total} paciente${total === 1 ? '' : 's'} registrado${total === 1 ? '' : 's'}` : 'Gestiona la información de tus pacientes'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowCreateModal(true)}
        >
          Nuevo Paciente
        </Button>
      </Box>

      {/* Filtros y búsqueda */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
            {/* Búsqueda */}
            <Box sx={{ minWidth: '250px', flex: '1 1 250px' }}>
              <TextField
                fullWidth
                placeholder="Buscar por nombre, apellido o RUT..."
                value={filters.search || ''}
                onChange={(e) => handleSearch(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Box>

            {/* Ordenamiento */}
            <Box sx={{ minWidth: '200px', flex: '1 1 200px' }}>
              <FormControl fullWidth>
                <InputLabel>Ordenar por</InputLabel>
                <Select
                  value={filters.sortBy || 'createdAt'}
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                  label="Ordenar por"
                >
                  {SORT_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Orden */}
            <Box sx={{ minWidth: '200px', flex: '1 1 200px' }}>
              <FormControl fullWidth>
                <InputLabel>Orden</InputLabel>
                <Select
                  value={filters.order || 'DESC'}
                  onChange={(e) => handleFilterChange('order', e.target.value)}
                  label="Orden"
                >
                  <MenuItem value="ASC">Ascendente</MenuItem>
                  <MenuItem value="DESC">Descendente</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Botón limpiar */}
            <Button variant="outlined" onClick={clearFilters}>
              Limpiar filtros
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabla de pacientes */}
      {!loading && patients.length === 0 ? (
        <Card>
          <CardContent sx={{ py: 8, textAlign: 'center' }}>
            <EmptyIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No se encontraron pacientes
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {filters.search ? 'Prueba con otro término de búsqueda.' : 'Comienza agregando tu primer paciente.'}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowCreateModal(true)}
            >
              Nuevo Paciente
            </Button>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Card}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>RUT</TableCell>
                <TableCell>Fecha Nac.</TableCell>
                <TableCell>Género</TableCell>
                <TableCell>Evaluador</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {patients.map((patient) => (
                <TableRow key={patient.id} hover>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar
                        sx={{
                          width: 32,
                          height: 32,
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          bgcolor: getAvatarColor(patient.id),
                        }}
                      >
                        {patient.firstName.charAt(0)}{patient.lastName.charAt(0)}
                      </Avatar>
                      <Typography variant="subtitle2">
                        {patient.firstName} {patient.lastName}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{patient.identificationNumber}</TableCell>
                  <TableCell>{formatDate(patient.dateOfBirth)}</TableCell>
                  <TableCell>
                    <Chip label={getGenderLabel(patient.gender)} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    {patient.evaluator ? (
                      `${patient.evaluator.firstName} ${patient.evaluator.lastName}`
                    ) : (
                      'N/A'
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Box display="flex" gap={0.5} justifyContent="flex-end">
                      <Tooltip title="Ver detalles">
                        <IconButton
                          size="small"
                          component={RouterLink}
                          to={`/patients/${patient.id}`}
                          color="primary"
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar paciente">
                        <IconButton
                          size="small"
                          onClick={() => openEditModal(patient)}
                          color="default"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar paciente">
                        <IconButton
                          size="small"
                          onClick={() => openDeleteModal(patient)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Paginación */}
      {!loading && patients.length > 0 && (
        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          mt={3}
        >
          <Typography variant="body2" color="text.secondary">
            Mostrando {patients.length} de {total} pacientes
          </Typography>
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          )}
        </Box>
      )}

      {/* Modales */}
      
      {/* Modal de creación */}
      <Dialog
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Crear Nuevo Paciente</DialogTitle>
        <DialogContent>
          <PatientForm
            onSubmit={handleCreatePatient}
            onCancel={() => setShowCreateModal(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Modal de edición */}
      <Dialog
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Editar Paciente</DialogTitle>
        <DialogContent>
          {editingPatient && (
            <PatientForm
              patient={editingPatient}
              onSubmit={handleEditPatient}
              onCancel={() => setShowEditModal(false)}
              isEditing
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Modal de eliminación */}
      <Dialog
        open={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que quieres eliminar al paciente{' '}
            <strong>
              {selectedPatient?.firstName} {selectedPatient?.lastName}
            </strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta acción marcará al paciente como inactivo.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button onClick={handleDeletePatient} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PatientList; 